import _get from 'lodash/get';
import { TranslateFunction } from 'react-localize-redux';

import {
  FulfilmentStatus,
  OrderHistoryData,
} from '../core/state/order/order.types';
import * as Api from '../weborder/Api';
import { CollectionMap, DeliveryMap } from '../weborder/Store';
import {
  ORDER_STATUS_FIRST_STEP_POLL_DELAY,
  ORDER_STATUS_POLL_DELAY,
} from './constants';
import {
  dateTimeFormats,
  formatDateTimeString,
  getDateFromMsDate,
  msToIanaTimezoneName,
  utcToZoned,
} from './dateTimeUtils';
import { checkCountryCode } from './locationUtils';
import { deliveryKey } from './makeUtils';

export const getOrderStatusPollDelay = (
  orderStatus: FulfilmentStatus | undefined,
): number => {
  if (orderStatus && !orderStatus.IsError) {
    if (orderStatus.StatusPosition === 0) {
      return ORDER_STATUS_FIRST_STEP_POLL_DELAY;
    }
    if (orderStatus.StatusPosition < orderStatus.TotalStatuses) {
      return ORDER_STATUS_POLL_DELAY;
    }
  }
  return null;
};

export const getDeliveryOrderDisplayTime = (
  deliveryTime: string,
  order: Api.PreviousOrderDm | OrderHistoryData,
  restaurantList: DeliveryMap,
  isError?: boolean,
): string => {
  const deliveryRestaurantKey = deliveryKey(
    order.VirtualRestaurantId,
    order.DeliveryLocationId,
  );
  const restaurant = _get(restaurantList, deliveryRestaurantKey);
  let orderTime: string;

  if (restaurant && restaurant.status === 'success') {
    orderTime = getOrderTime(order, deliveryTime, restaurant.data, isError);
  }

  return orderTime;
};

export const getCollectionOrderDisplayTime = (
  collectionTime: string,
  restaurantList: CollectionMap,
  order: Api.PreviousOrderDm | OrderHistoryData,
  isError?: boolean,
): string => {
  const restaurant = _get(restaurantList, order.PhysicalRestaurantId);
  let orderTime: string;

  if (restaurant && restaurant.status === 'success') {
    orderTime = getOrderTime(order, collectionTime, restaurant.data, isError);
  }

  return orderTime;
};

const getOrderTime = (
  order: Api.PreviousOrderDm | OrderHistoryData,
  orderTime: string,
  restaurant: Api.CollectionRestaurantDetail | Api.DeliveryRestaurantDetail,
  isError?: boolean,
): string => {
  const timeZone = msToIanaTimezoneName(restaurant.TimeZoneInfoId);
  const displayTimeString = isError
    ? order.TsOrderPlaced.toString()
    : orderTime;
  const displayTimeDate = getDateFromMsDate(displayTimeString);
  const utcTimeToTimeZone = utcToZoned(displayTimeDate, timeZone);

  return formatDateTimeString(
    utcTimeToTimeZone,
    dateTimeFormats.standardDateTime,
  );
};

export const getOrderCollectionOrDeliveryTime = (
  order: Api.PreviousOrderDm | OrderHistoryData,
  orderStatus: FulfilmentStatus,
  collectionRestaurants: CollectionMap,
  deliveryRestaurants: DeliveryMap,
  translate: TranslateFunction,
): string => {
  if (orderStatus?.StatusPosition === 0 && !orderStatus?.IsError) {
    return translate('OrderComplete_Pending') as string;
  }

  if (order && (orderStatus?.EstimatedCompletionTime || orderStatus?.IsError)) {
    const time =
      order.DeliveryType === Api.DeliveryType.Delivery
        ? getDeliveryOrderDisplayTime(
            orderStatus?.EstimatedCompletionTime,
            order,
            deliveryRestaurants,
            orderStatus?.IsError,
          )
        : getCollectionOrderDisplayTime(
            orderStatus?.EstimatedCompletionTime,
            collectionRestaurants,
            order,
            orderStatus?.IsError,
          );
    return time;
  }

  return undefined;
};

export const getOrderStatusViewedEventData = (
  fulfilmentStatus: FulfilmentStatus,
  orderId: number,
): {
  amp_label: string;
  amp_orderStatus: string;
  amp_orderId: number;
  amp_statusPosition: number;
  amp_totalStatuses: number;
} => {
  const {
    StatusName: statusName,
    StatusPosition: statusPosition,
    TotalStatuses: totalStatuses,
  } = fulfilmentStatus;

  return {
    amp_label: 'Order Status Viewed',
    amp_orderStatus: statusName,
    amp_orderId: orderId,
    amp_statusPosition: statusPosition,
    amp_totalStatuses: totalStatuses,
  };
};

export const getTaxLabel = (
  configData: Api.AppConfig,
  translate: TranslateFunction,
): string => {
  const isAustralianWhiteLabel = checkCountryCode(configData, 'AU');
  const taxLabel = isAustralianWhiteLabel
    ? translate('OrderDetails_gstIncluded')
    : translate('Tax');

  return taxLabel as string;
};
